import { floor } from 'lodash';
import { isSpaceBounded, Locale } from '../models/Base';

export const getCharacterCount = (str: string) => [...str].length;

export const caseInsensitiveSearch = (str: string, target: string) => {
  return str.search(new RegExp(target, 'i'));
};

export const caseInsensitiveCompare = (
  a: string,
  b: string,
  ignorePunctuation = true,
) => {
  const _a = ignorePunctuation ? trimPunctuation(a) : a;
  const _b = ignorePunctuation ? trimPunctuation(b) : b;
  return _a.toLocaleLowerCase() === _b.toLocaleLowerCase();
};

export const caseInsensitiveIncludes = (
  str: string,
  subString: string,
): boolean => str.toLocaleLowerCase().includes(subString.toLocaleLowerCase());

export const convertByteToReadable = (byteSize: number): string => {
  let i = Math.floor(Math.log(byteSize) / Math.log(1024));
  const byteSymbols = ['B', 'KB', 'MB', 'GB', 'TB'];
  if (i === -Infinity) {
    i = 0;
  } else if (i > 4) {
    i = 4;
  }
  const byteSymbol = byteSymbols[i];
  if (byteSymbol) {
    return `${floor(byteSize / Math.pow(1024, i), 2) * 1} ${byteSymbol}`;
  }
  return '';
};

export const isPunctuation = (str: string, forSentence = false): boolean => {
  const punctuations = forSentence
    ? /^[.?!;。？！；]$/
    : /^[,.?!;:'"\-~()[\]{}，。？！；：‘’“”—～『』「」（）［］｛｝、]$/;
  return !!str.match(punctuations);
};

export const includesPunctuation = (
  str: string,
  forSentence = false,
): boolean => {
  const punctuations = forSentence
    ? /[.?!;。？！；]/
    : /[,.?!;:'"\-~()[\]{}，。？！；：‘’“”—～『』「」（）［］｛｝、]/;
  return str.search(punctuations) !== -1;
};

export const trimPunctuation = (str: string): string => {
  return str.replace(
    /^[,.?!;:'"\-~()[\]{}，。？！；：‘’“”—～『』「」（）［］｛｝、]+|[,.?!;:'"\-~()[\]{}，。？！；：‘’“”—～『』「」（）［］｛｝、]+$/g,
    '',
  );
};

export const obfuscateEmail = (email: string): string => {
  const parts = email.split('@');
  if (parts[0] && parts[1]) {
    if (parts[0].length > 3 && parts[1].length > 3) {
      return parts[0].substring(0, 3) + '***@***' + parts[1].substring(3);
    } else if (parts[0].length > 3) {
      return parts[0].substring(0, 3) + '***@' + parts[1];
    } else if (parts[1].length > 3) {
      return parts[0] + '@***' + parts[1].substring(3);
    }
  }
  return email;
};

export const trimStringToLength = (text: string, length: number) => {
  return text.length > length ? text.substring(0, length - 3) + '...' : text;
};

export const splitPunctuation = (str: string): string => {
  return str.replace(/([,.?!;:，。？！；：、]+)/g, ' $1 ');
};

export const hashCode = (s: string): number => {
  let hash = 0;
  /* eslint-disable no-bitwise */
  for (let i = 0; i < s.length; i += 1) {
    const char = s.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  /* eslint-enable no-bitwise */
  return hash < 0 ? hash + 2 ** 32 : hash;
};

export const isLargeSpace = (str: string) => {
  // eslint-disable-next-line no-irregular-whitespace
  return !!str.match(/[　]$/);
};

export const isNewLine = (str: string) => {
  return !!str.match(/[\n]$/);
};

export const isSentenceFinished = (word: string) =>
  includesPunctuation(word, true) || isLargeSpace(word) || isNewLine(word);

export const stringToNumber = (str: string): number | string => {
  const number = Number(str.replace(/,/g, ''));
  if (isNaN(number)) {
    return str;
  } else {
    return number;
  }
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
export const splitNumberAndText: {
  (str: string, string?: false): (number | string)[];
  (str: string, string: true): string[];
} = (str, string = false) => {
  const regNumber = /(-\d+|\d+)(,\d+)*(\.\d+)*/g;
  const res: (number | string)[] = [];
  let lastIndex = 0;
  str.replace(
    regNumber,
    (match: string, _p1: string, _p2: string, _p3: string, offset: number) => {
      const text = str.substring(lastIndex, offset);
      if (text.length > 0) {
        res.push(text);
      }

      // number
      if (string) {
        res.push(match);
      } else {
        res.push(Number(match.replace(/,/g, '')));
      }
      lastIndex = offset + match.length;
      return match;
    },
  );
  if (lastIndex < str.length) {
    res.push(str.substring(lastIndex));
  }
  if (string) {
    return res as string[];
  } else {
    return res;
  }
};

export const splitRedeemMailingAddress = (str = '') => {
  const [name, street, rest] = str.split('\n');
  let street1, street2, city, province, code;
  if (street) {
    [street1, street2] = street.split('_');
  }
  if (rest) {
    [city, province, code] = rest.split('_');
  }
  return {
    name: name ?? '',
    street1: street1 ?? '',
    street2: street2 ?? '',
    city: city ?? '',
    province: province ?? '',
    code: code ?? '',
  };
};

// url
export const isUrl = (urlString: string): boolean => {
  const protocolAndDomainRE = /^(?:\w+:)?\/\/(\S+)$/;
  const localhostDomainRE = /^localhost[:?\d]*(?:[^:?\d]\S*)?$/;
  const nonLocalhostDomainRE = /^[^\s.]+\.\S{2,}$/;

  const match = urlString.match(protocolAndDomainRE);
  if (!match) {
    // If the protocol is missing, check if the rest is a valid domain or localhost
    return (
      localhostDomainRE.test(urlString) || nonLocalhostDomainRE.test(urlString)
    );
  }

  const everythingAfterProtocol = match[1];
  if (!everythingAfterProtocol) {
    return false;
  }

  return (
    localhostDomainRE.test(everythingAfterProtocol) ||
    nonLocalhostDomainRE.test(everythingAfterProtocol)
  );
};

export const ensureProtocol = (url: string) => {
  // Regular expression to check if the URL starts with a protocol
  const protocolRE = /^(?:\w+:)?\/\//;
  return protocolRE.test(url) ? url : `https://${url}`;
};

export const isNotEmptyString = (str: string) => {
  return /\S/.test(str);
};

export const containsPunctuation = (str: string) => {
  const punctuationRegex = /\p{P}/gu;
  return punctuationRegex.test(str);
};

export const containsOnlyPunctuation = (str: string) => {
  const punctuationRegex = /^[\p{P}]+$/gu;
  return punctuationRegex.test(str);
};

// TODO: need to solve number contains in emoji
export const containsEmoji = (str: string) => {
  const emojiRegex = /[\p{Emoji}]/gu;
  return emojiRegex.test(str);
};

export const containsOnlyEmoji = (str: string) => {
  const numberRegex = /\d/gu;
  const emojiRegex = /^[\p{Emoji}]+$/gu;
  return emojiRegex.test(str) && !numberRegex.test(str);
};

export const containsOnlyNotWord = (str: string) => {
  return containsOnlyEmoji(str) || containsOnlyPunctuation(str);
};

export const splitIntoGraphme = (
  text: string,
  locale: Locale,
  { otherLocales }: { otherLocales?: Locale[] } = {},
) => {
  if (typeof Intl.Segmenter !== 'undefined') {
    // Use Intl.Segmenter if available
    const segmenter = new Intl.Segmenter([locale, ...(otherLocales ?? [])], {
      granularity: 'grapheme',
    });
    const segments = Array.from(segmenter.segment(text))
      .map((item) => {
        return {
          index: item.index,
          text: item.segment,
          isWordLike: !containsOnlyNotWord(item.segment),
        };
      })
      .filter((item) => isNotEmptyString(item.text));
    return segments;
  } else {
    // Fallback: Split by spaces for basic word segmentation
    const segments = [...text]
      // .filter((str) => isNotEmptyString(str))
      .map((str, index) => {
        return {
          index: index,
          text: str,
          isWordLike: !containsOnlyNotWord(str),
        };
      });
    return segments;
  }
};

export const splitIntoWords = (
  text: string,
  locale: Locale,
  {
    otherLocales,
    withSpace = true,
  }: { otherLocales?: Locale[]; withSpace?: boolean } = {},
) => {
  if (typeof Intl.Segmenter !== 'undefined') {
    // Use Intl.Segmenter if available
    const segmenter = new Intl.Segmenter([locale, ...(otherLocales ?? [])], {
      granularity: 'word',
    });
    const segments = Array.from(segmenter.segment(text))
      .map((item) => {
        return {
          index: item.index,
          text: item.segment,
          isWordLike: item.isWordLike, // only word mode has isWordLike
        };
      })
      .filter((item) => withSpace || isNotEmptyString(item.text));
    return segments;
  } else {
    // Fallback: Split by spaces for basic word segmentation

    const split: string[] = isSpaceBounded(locale)
      ? text.split(/\s+/)
      : [...text];

    const segments = split
      .filter((str) => withSpace || isNotEmptyString(str))
      .map((str, index) => {
        return {
          index: index,
          text: str,
          isWordLike: !containsOnlyNotWord(str),
        };
      });
    return segments;
  }
};

export const splitIntoSentence = (
  text: string,
  locale: Locale,
  { otherLocales }: { otherLocales?: Locale[] } = {},
) => {
  if (typeof Intl.Segmenter !== 'undefined') {
    // Use Intl.Segmenter if available
    const segmenter = new Intl.Segmenter([locale, ...(otherLocales ?? [])], {
      granularity: 'sentence',
    });
    const segments = Array.from(segmenter.segment(text)).map((item) => {
      return {
        index: item.index,
        text: item.segment,
        isWordLike: !containsOnlyNotWord(item.segment),
      };
    });
    // .filter((item) => isNotEmptyString(item.text));
    return segments;
  } else {
    // Fallback: Split by spaces for basic word segmentation

    const stopPunctuation = ',.?!;，。？！；';
    const punctuationRegex = new RegExp('[' + stopPunctuation + ']', 'g');

    const segments = text.split(punctuationRegex).map((str, index) => {
      return {
        index: index,
        text: str, // TODO: add the punctuation at last?
        isWordLike: !containsOnlyNotWord(str),
      };
    });
    return segments;
  }
};

export const splitIntoChunks = (
  str: string,
  locale: Locale,
  limitation: number,
) => {
  if (!(limitation > 0 && str)) {
    return [];
  }

  const result = [];
  let currentChunk = '';
  let currentWordCount = 0;
  let currentNewLineCount = 0;

  const sentences = splitIntoSentence(str, locale);
  sentences.forEach((sentence) => {
    const token = sentence.text;
    if (token) {
      const tokenWords = splitIntoWords(token, locale);
      const onlyTokenWords = splitIntoWords(token, locale, {
        withSpace: false,
      });
      const hasNewLine = token.indexOf('\n') > -1;

      if (currentWordCount + onlyTokenWords.length > limitation) {
        if (currentChunk) {
          result.push(currentChunk.trim());
          currentChunk = '';
          currentWordCount = 0;
          currentNewLineCount = 0;
        }
      }

      if (onlyTokenWords.length > limitation) {
        let remainingWordsCount = onlyTokenWords.length;
        let startWordCount = 0;
        const currentChunkWordCount = splitIntoWords(currentChunk, locale, {
          withSpace: false,
        }).length;
        const partCount = limitation - currentChunkWordCount;
        while (remainingWordsCount > 0) {
          const targetStartCount = tokenWords.findIndex(
            (word) => onlyTokenWords[startWordCount]?.index === word.index,
          );
          const targetEndCount = tokenWords.findIndex(
            (word) =>
              onlyTokenWords[startWordCount + partCount]?.index === word.index,
          );
          const part = tokenWords
            .slice(targetStartCount, targetEndCount)
            .map((t) => t.text)
            .join('');
          result.push(currentChunk + part);
          currentNewLineCount = 0;
          remainingWordsCount = remainingWordsCount - partCount;
          if (remainingWordsCount > limitation) {
            startWordCount = startWordCount + partCount;
          } else {
            currentChunk = tokenWords
              .slice(targetEndCount)
              .map((t) => t.text)
              .join('');
            remainingWordsCount = 0;
            currentNewLineCount = hasNewLine ? 1 : 0;
          }
        }
      } else {
        currentChunk = currentChunk + token;
        currentWordCount = splitIntoWords(currentChunk, locale, {
          withSpace: false,
        }).length;
        currentNewLineCount = currentNewLineCount + (hasNewLine ? 1 : 0);
      }

      if (currentNewLineCount === 5) {
        const lines = currentChunk.split('\n');
        let currentStr = '';

        lines.forEach((line, index) => {
          if (index < 5) {
            currentStr += line + '\n';
          } else {
            result.push(currentStr);
            currentStr = line;
          }
        });

        currentChunk = currentStr;
        currentWordCount = splitIntoWords(currentChunk, locale, {
          withSpace: false,
        }).length;
        currentNewLineCount = 0;
      }

      if (
        splitIntoWords(currentChunk, locale, { withSpace: false }).length ===
        limitation
      ) {
        result.push(currentChunk.trim());
        currentChunk = '';
        currentWordCount = 0;
        currentNewLineCount = 0;
      }
    }
  });

  if (currentChunk) {
    result.push(currentChunk.trim());
  }

  return result;
};

export const selectRandomWords = (
  words: string[],
  weights: number[],
  wordCount: number,
) => {
  const totalWeight = weights.reduce((acc, curr) => acc + curr, 0);
  const selectedIndices: number[] = [];
  const tempWeights = [...weights];
  const selectedSet = new Set<number>();

  let iteratorCount = 0;
  const maxIteratorCount = 1000;

  while (
    selectedIndices.length < wordCount &&
    iteratorCount < maxIteratorCount
  ) {
    let randomWeight = Math.floor(Math.random() * totalWeight);

    for (let index = 0; index < tempWeights.length; index++) {
      const weight = tempWeights[index];
      if (weight) {
        if (randomWeight < weight) {
          if (
            !selectedSet.has(index) &&
            words[index] &&
            !selectedIndices.includes(index)
          ) {
            selectedIndices.push(index);
            selectedSet.add(index);
          }
          break;
        } else {
          randomWeight -= weight;
        }
      }
    }

    for (const selectedIndex of selectedSet) {
      tempWeights[selectedIndex] = 0;
    }

    iteratorCount++;
  }

  return selectedIndices;
};
